import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { 
  Target, Search, BookOpen, Zap, ExternalLink, Play, 
  CheckCircle, Clock, ChevronRight, Edit, Plus, 
  List, Lightbulb, ArrowRight, AlertCircle
} from 'lucide-react';
import mathJaxConfig from '../components/mathJaxConfig';
import PlanAction from './PlanAction';
import ReactMarkdown from 'react-markdown';

const ActionSpace = ({ onCompetenceLoaded, onExerciseComplete, userId }) => {
  const { competenceId } = useParams();
  const navigate = useNavigate();
  const [competence, setCompetence] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeStep, setActiveStep] = useState(null);
  const [isManagingPlan, setIsManagingPlan] = useState(false);

  // Flag pour éviter les appels multiples
  const [planUpdated, setPlanUpdated] = useState(false);
  
  // Refs pour éviter les boucles
  const hasNotifiedRef = useRef(false);
  const lastLoadedCompetenceId = useRef(null);
  
  // Fonction de notification memoized pour éviter les re-créations
  const notifyCompetenceLoaded = useCallback((competenceData) => {
    if (onCompetenceLoaded && !hasNotifiedRef.current) {
      console.log('ActionSpace: Notification de la compétence chargée à App.js');
      hasNotifiedRef.current = true;
      onCompetenceLoaded(competenceData);
    }
  }, [onCompetenceLoaded]);

  // Effet pour recharger les données quand le plan est modifié
  useEffect(() => {
    if (planUpdated && competenceId && userId) {
      const refreshData = async () => {
        try {
          console.log('ActionSpace: Refreshing data after plan update');
          const docRef = doc(db, 'users', userId, 'competences', competenceId);
          const docSnap = await getDoc(docRef);
          
          if (docSnap.exists()) {
            const competenceData = { ...docSnap.data(), id: competenceId };
            setCompetence(competenceData);
            // Ne pas re-notifier après une mise à jour de plan
            setPlanUpdated(false);
          }
        } catch (err) {
          console.error('Erreur lors de la mise à jour des données:', err);
          setError('Erreur lors de la mise à jour des données');
        }
      };
      
      refreshData();
    }
  }, [planUpdated, competenceId, userId]); // Removed onCompetenceLoaded from dependencies

  // Effet principal pour charger l'objectif
  useEffect(() => {
    const checkAuth = () => {
      const user = auth.currentUser;
      if (!user) {
        navigate('/');
        return false;
      }
      return true;
    };

    const fetchCompetence = async () => {
      // Ne pas charger si on n'a pas les infos nécessaires
      if (!checkAuth() || !competenceId || !userId) {
        setLoading(false);
        return;
      }

      // Éviter de recharger la même compétence
      if (lastLoadedCompetenceId.current === competenceId && competence) {
        setLoading(false);
        return;
      }

      try {
        console.log('ActionSpace: Chargement de la compétence', competenceId);
        const docRef = doc(db, 'users', userId, 'competences', competenceId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const competenceData = { ...docSnap.data(), id: competenceId };
          console.log('ActionSpace: Compétence chargée:', competenceData.title);
          
          setCompetence(competenceData);
          lastLoadedCompetenceId.current = competenceId;
          
          // Notifier seulement au premier chargement
          notifyCompetenceLoaded(competenceData);
        } else {
          setError('Aucune compétence trouvée');
        }
      } catch (err) {
        console.error('Erreur de chargement:', err);
        setError('Erreur lors de la récupération des données');
      } finally {
        setLoading(false);
      }
    };

    // Reset les flags quand l'ID change
    if (lastLoadedCompetenceId.current !== competenceId) {
      hasNotifiedRef.current = false;
      setError(null);
      setLoading(true);
    }

    fetchCompetence();
  }, [competenceId, userId, navigate]); // Removed onCompetenceLoaded and competence from dependencies

  // Reset notification flag when competenceId changes
  useEffect(() => {
    hasNotifiedRef.current = false;
  }, [competenceId]);

  const handleDeleteCompetence = async () => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette compétence ?')) {
      try {
        // Logique de suppression à implémenter
        navigate('/dashboard');
      } catch (err) {
        console.error('Erreur lors de la suppression:', err);
        setError('Erreur lors de la suppression');
      }
    }
  };

  const handleStepClick = (stepId) => {
    setActiveStep(activeStep === stepId ? null : stepId);
  };

  // Fonction pour obtenir le style des phases
  const getPhaseStyle = (phase) => {
    const phases = [
      { 
        id: 'ambition', 
        label: 'Ambition', 
        desc: 'Définition de l\'objectif', 
        color: 'from-purple-400 to-purple-600', 
        bgColor: 'bg-purple-50', 
        textColor: 'text-purple-700', 
        borderColor: 'border-purple-200',
        icon: <Target size={20} /> 
      },
      { 
        id: 'stratégie', 
        label: 'Stratégie', 
        desc: 'Recommandations disponibles', 
        color: 'from-blue-400 to-blue-600', 
        bgColor: 'bg-blue-50', 
        textColor: 'text-blue-700',
        borderColor: 'border-blue-200', 
        icon: <Target size={20} /> 
      },
      { 
        id: 'action', 
        label: 'Action', 
        desc: 'Mise en œuvre du plan d\'action', 
        color: 'from-green-400 to-green-600', 
        bgColor: 'bg-green-50', 
        textColor: 'text-green-700',
        borderColor: 'border-green-200', 
        icon: <Target size={20} /> 
      }
    ];
    
    const phaseObj = phases.find(p => p.id === phase) || phases[0];
    return {
      color: phaseObj.color,
      bgColor: phaseObj.bgColor,
      textColor: phaseObj.textColor,
      borderColor: phaseObj.borderColor,
      icon: phaseObj.icon
    };
  };

  // Fonction pour générer des ressources pertinentes pour chaque étape
  const getStepResources = (step) => {
    return [
      {
        type: 'exploration',
        title: 'Explorer les concepts clés',
        description: `Approfondir les notions essentielles liées à "${step.titre}"`,
        icon: BookOpen,
        color: 'blue',
        action: () => navigate(`/cours/${competenceId}?topic=${encodeURIComponent(step.titre)}`)
      },
      {
        type: 'quiz',
        title: 'Quiz interactif',
        description: 'Testez vos connaissances par des exercices pratiques',
        icon: Play,
        color: 'orange',
        action: () => navigate(`/quiz/${competenceId}?topic=${encodeURIComponent(step.titre)}`)
      },
      {
        type: 'inspiration',
        title: 'Rechercher des inspirations',
        description: 'Découvrir des idées et exemples concrets',
        icon: Search,
        color: 'purple',
        action: () => navigate(`/inspiration/${competenceId}?query=${encodeURIComponent(step.titre)}`)
      },
      {
        type: 'external',
        title: 'Ressources recommandées',
        description: 'Consulter des sites et articles sélectionnés',
        icon: ExternalLink,
        color: 'green',
        links: [
          { url: 'https://example.com/resource1', title: 'Guide pratique' },
          { url: 'https://example.com/resource2', title: 'Article spécialisé' },
          { url: 'https://example.com/resource3', title: 'Tutoriel vidéo' }
        ]
      }
    ];
  };

  const renderPlanWithResources = () => {
    if (!competence?.actionPlan?.steps || competence.actionPlan.steps.length === 0) {
      return (
        <div className="w-full bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow-lg border border-gray-200 p-8 mb-8 text-center">
          <Zap className="w-16 h-16 text-orange-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Aucun plan d'action disponible</h3>
          <p className="text-gray-600 mb-6 main-text">Créez votre premier plan d'action pour commencer votre parcours.</p>
          <button
            onClick={() => setIsManagingPlan(true)}
            className="px-6 py-3 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white inline-flex items-center gap-2 transition-all duration-200 shadow-md hover:shadow-lg mx-auto"
          >
            <Plus className="w-5 h-5" />
            <span>Créer un plan d'action</span>
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-8 mb-8">
        {competence.actionPlan.steps.map((step) => {
          const resources = getStepResources(step);
          const isActive = activeStep === step.id;
          
          return (
            <div key={step.id} className="w-full bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
              <div 
                className={`p-6 cursor-pointer transition-colors ${
                  step.termine ? 'bg-green-50 border-b border-green-100' : 'border-b border-gray-100'
                } ${isActive ? 'bg-indigo-50' : ''}`}
                onClick={() => handleStepClick(step.id)}
              >
                <div className="flex items-start">
                  <div className={`w-8 h-8 rounded-full mr-4 flex items-center justify-center flex-shrink-0 ${
                    step.termine ? 'bg-green-500 text-white' : 'border-2 border-indigo-300'
                  }`}>
                    {step.termine && <CheckCircle className="w-5 h-5" />}
                    {!step.termine && <span className="text-indigo-600 font-medium">{step.id}</span>}
                  </div>
                  
                  <div className="flex-grow">
                    <h3 className={`text-xl font-semibold ${step.termine ? 'text-gray-500 line-through' : 'text-gray-800'}`}>
                      {step.titre}
                    </h3>
                    
                    <div className="flex items-center text-sm text-gray-500 mt-1">
                      <Clock className="w-4 h-4 mr-1" />
                      <span>{step.duree}</span>
                    </div>
                  </div>
                  
                  <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isActive ? 'rotate-90' : ''}`} />
                </div>
              </div>
              
              {isActive && (
                <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-b border-indigo-100">
                  <div className="prose max-w-none text-gray-700 mb-6">
                      <ReactMarkdown>{step.description}</ReactMarkdown>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                    {resources.map((resource, index) => {
                      if (resource.type === 'external') {
                        return (
                          <div key={index} className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm hover:shadow transition-shadow">
                            <div className="flex items-center mb-3">
                              <div className={`w-10 h-10 rounded-full bg-${resource.color}-100 flex items-center justify-center mr-3`}>
                                <resource.icon className={`w-5 h-5 text-${resource.color}-600`} />
                              </div>
                              <h4 className="font-semibold text-gray-800">{resource.title}</h4>
                            </div>
                            <p className="text-sm text-gray-600 mb-3">{resource.description}</p>
                            <div className="space-y-2">
                              {resource.links.map((link, linkIndex) => (
                                <a 
                                  key={linkIndex}
                                  href={link.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="flex items-center text-sm text-indigo-600 hover:text-indigo-800"
                                >
                                  <ExternalLink className="w-3 h-3 mr-1" />
                                  {link.title}
                                </a>
                              ))}
                            </div>
                          </div>
                        );
                      }
                      
                      return (
                        <button 
                          key={index}
                          onClick={resource.action}
                          className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow flex items-start"
                        >
                          <div className={`w-10 h-10 rounded-full bg-${resource.color}-100 flex items-center justify-center mr-3`}>
                            <resource.icon className={`w-5 h-5 text-${resource.color}-600`} />
                          </div>
                          <div className="text-left">
                            <h4 className="font-semibold text-gray-800">{resource.title}</h4>
                            <p className="text-sm text-gray-600">{resource.description}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  
                  {!step.termine && (
                    <div className="mt-6 flex justify-end">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          // Marquer l'étape comme terminée
                          const updatedSteps = competence.actionPlan.steps.map(s => 
                            s.id === step.id ? { ...s, termine: true } : s
                          );
                          const updatedCompetence = {
                            ...competence,
                            actionPlan: {
                              ...competence.actionPlan,
                              steps: updatedSteps
                            }
                          };
                          setCompetence(updatedCompetence);
                          // Note: Ici vous devriez aussi mettre à jour Firestore
                        }}
                        className="px-4 py-2 bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 transition-colors flex items-center gap-2"
                      >
                        <CheckCircle className="w-4 h-4" />
                        <span>Marquer comme terminé</span>
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Définition des styles de police
  const fontStyles = `
    .page-title {
      font-size: 1.75rem;
      line-height: 2.25rem;
      font-weight: 700;
    }
    
    .section-title {
      font-size: 1.5rem;
      line-height: 2rem;
      font-weight: 600;
    }
    
    .main-text {
      font-size: 1.125rem;
      line-height: 1.75rem;
    }
    
    .objective-text {
      font-size: 1.125rem;
      line-height: 1.75rem;
    }
    
    textarea.input-text {
      font-size: 1.125rem !important;
    }
    
    .button-text {
      font-size: 1rem;
    }
    
    .objective-card-title {
      font-size: 1.25rem;
      line-height: 1.6;
      font-weight: 500;
      white-space: normal;
      overflow: visible;
      text-overflow: clip;
      max-height: none;
      -webkit-line-clamp: unset;
      -webkit-box-orient: unset;
      display: block;
    }
  `;

  if (loading) {
    return (
      <div className="flex justify-center items-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          <div>
            <p className="font-semibold">Une erreur est survenue</p>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Déterminer la phase actuelle de l'objectif
  const determinePhase = () => {
    if (!competence) return 'ambition';
    
    if (competence.actionPlan?.steps?.length > 0) {
      return 'action';
    } else if (competence.recommendation) {
      return 'stratégie';
    } else {
      return 'ambition';
    }
  };

  // Obtenir le style de la phase
  const currentPhase = determinePhase();
  const phaseStyle = getPhaseStyle(currentPhase);

  return (
      <div className="bg-gray-50 min-h-screen">
        {/* Injecter les styles de police */}
        <style>{fontStyles}</style>
        
        {/* Fil d'Ariane avec liens actifs */}
        <div className="w-full bg-white border-b border-gray-200 px-6 py-3 mb-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center text-sm">
              <Link to="/Ambition" className="text-gray-600 hover:text-indigo-600 transition-colors">
                Ambition
              </Link>
              <ChevronRight className="h-4 w-4 mx-1 text-gray-400" />
                  <Link to={`/Recommandations/${competenceId}`} className="text-gray-600 hover:text-indigo-600 transition-colors">
                Stratégie
              </Link>
              <ChevronRight className="h-4 w-4 mx-1 text-gray-400" />
              <span className="text-indigo-600 font-medium">Plan d'actions</span>
            </div>
          </div>
        </div>

        {competence && (
          <div className="max-w-6xl mx-auto px-6 py-6">
            {/* Carte d'objectif */}
            <div className="w-full bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow-lg border border-gray-200 p-6 mb-8">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                  <Zap  className="w-6 h-6 text-orange-600" />
                </div>
                <div className="flex-grow">
                  <div className="flex flex-wrap items-center gap-3 mb-3">
                    <h1 className="page-title text-gray-800 mr-2">
                      Plan d'actions pour réaliser mon objectif
                    </h1>
                    
                    {competence.type && (
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        competence.type === 'project' ? 'bg-purple-50 text-purple-700' :
                        competence.type === 'idea' ? 'bg-blue-50 text-blue-700' :
                        competence.type === 'desire' ? 'bg-indigo-50 text-indigo-700' :
                        'bg-green-50 text-green-700'
                      }`}>
                        {competence.type === 'project' ? 'Projet' :
                         competence.type === 'idea' ? 'Idée' :
                         competence.type === 'desire' ? 'Désir' :
                         competence.type === 'skill' ? 'Compétence' : 'Objectif'}
                      </span>
                    )}
                    
                    {competence.theme && (
                      <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-sm">
                        {competence.theme}
                      </span>
                    )}
                  </div>
                  
                  <h2 className="section-title text-gray-700 mb-2 break-words">
                    {competence.title || competence.shortTitle}
                  </h2>
                  {competence.description && (
                    <p className="main-text text-gray-600 mt-1">
                      {competence.description}
                    </p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Plan d'action avec ressources associées ou interface de gestion */}
            {isManagingPlan ? (
              userId && (
                <PlanAction 
                  userId={userId} 
                  onDelete={handleDeleteCompetence}
                  onPlanUpdate={() => {
                    setPlanUpdated(true);
                    setIsManagingPlan(false);
                  }}
                  startInEditMode={true}
                />
              )
            ) : (
              <>
                {renderPlanWithResources()}
                
                {/* Bouton de modification en bas */}
                <div className="flex justify-end mt-6">
                  <button
                    onClick={() => setIsManagingPlan(!isManagingPlan)}
                    className="px-4 py-2 rounded-lg flex items-center gap-2 transition-all duration-200 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-sm"
                  >
                    <Edit className="w-4 h-4" />
                    <span>Modifier le plan</span>
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
  );
};

export default ActionSpace;